import * as React from 'react';
import { a as EGDSButton } from '../paging-button/index-chunk.esm.js';
import { E as EGDSPrimaryButton } from '../primary-button/index-chunk.esm.js';
import { E as EGDSFloatingFullWidthButton } from '../floating-full-width-button/index-chunk.esm.js';
import { E as EGDSSecondaryButton } from '../secondary-button/index-chunk.esm.js';
import { E as EGDSFloatingButton, a as EGDSFloatingActionButton } from '../floating-action-button/index-chunk.esm.js';
import { E as EGDSTertiaryButton } from '../tertiary-button/index-chunk.esm.js';
import { jsx, jsxs } from 'react/jsx-runtime';
import { c as classNames, d as EGDSContextProvider } from '../theme-provider/index-chunk.esm.js';
import styles from '../styles/uitk-egds-global-navigation.css';
import { v as validatePropType } from '../validate-prop-types/index-chunk.esm.js';
import { a as EGDSIcon } from '../icon/index-chunk.esm.js';
import { E as EGDSLink } from '../link/index-chunk.esm.js';
import { v as validateChildTypes } from '../validate-child-types/index-chunk.esm.js';
import { a as analyticsTracker } from '../analytics-tracker/index-chunk.esm.js';

const getClassNameList$7 = classNames.bind(styles);
class GlobalNavigationMenu extends React.Component {
    static displayName = 'GlobalNavigationMenu';
    menuContainerRef;
    constructor(props) {
        super(props);
        this.state = {
            animate: false,
            isOpen: false,
            mounted: false,
        };
        this.toggleMenu = this.toggleMenu.bind(this);
        this.handleEvent = this.handleEvent.bind(this);
    }
    componentDidMount() {
        if (typeof document !== 'undefined' && typeof document.body !== 'undefined') {
            document.body.addEventListener('click', this.handleEvent);
        }
        this.setState({
            mounted: true,
        });
    }
    componentWillUnmount() {
        if (typeof document !== 'undefined' && typeof document.body !== 'undefined') {
            document.body.removeEventListener('click', this.handleEvent);
            document.body.style.cursor = '';
        }
    }
    handleEvent(event) {
        const { isOpen } = this.state;
        if (isOpen) {
            this.clickEvent(event);
        }
    }
    clickEvent(e) {
        const { target } = e;
        const parent = this.menuContainerRef;
        const isDescendant = this.isDescendant(parent, target);
        const isTrigger = target.classList.contains('global-navigation-menu-checkbox');
        if (!isDescendant && !isTrigger) {
            this.toggleMenu();
        }
    }
    isDescendant(parent, child) {
        let node = child.parentNode;
        // eslint-disable-next-line no-unmodified-loop-condition
        while (node !== null && parent !== null) {
            if (node === parent || node === parent.parentNode) {
                return true;
            }
            node = node.parentNode;
        }
        return false;
    }
    toggleMenu() {
        const { isOpen, mounted } = this.state;
        const willOpen = isOpen === false;
        if (mounted) {
            if (typeof document !== 'undefined' && typeof document.body !== 'undefined') {
                if (willOpen) {
                    document.body.style.cursor = 'pointer';
                }
                else {
                    document.body.style.cursor = '';
                }
            }
            this.setState({
                animate: true,
                isOpen: willOpen,
            });
        }
    }
    render() {
        const { animate, isOpen, mounted } = this.state;
        const { children } = this.props;
        const baseCssClass = 'global-navigation-menu';
        const wrapperClassNames = getClassNameList$7(`${baseCssClass}-container`, {
            'animation-disabled': !animate,
        }, {
            [`${baseCssClass}-open`]: mounted && isOpen,
        });
        const menuClassNames = getClassNameList$7(baseCssClass, {
            [`${baseCssClass}-mounted`]: mounted,
        });
        const cssClassNames = getClassNameList$7(`${baseCssClass}-checkbox`, 'is-visually-hidden');
        const triggerProps = {
            className: cssClassNames,
            onClick: this.toggleMenu,
        };
        const menuChildren = React.Children.map(children, (child) => {
            let childProps;
            let containerChildProps;
            if (!React.isValidElement(child)) {
                return undefined;
            }
            switch (child.type) {
                case GlobalNavigationMenuTrigger:
                    childProps = child.props;
                    return (jsx(GlobalNavigationMenuTrigger, { ...childProps, mounted: mounted, onClick: this.toggleMenu, isOpen: isOpen }));
                case GlobalNavigationMenuContainer:
                    containerChildProps = child.props;
                    return (jsx(GlobalNavigationMenuContainer, { containerClasses: wrapperClassNames, 
                        // eslint-disable-next-line no-return-assign
                        containerRef: (el) => (this.menuContainerRef = el), ...containerChildProps, isOpen: isOpen }));
            }
        });
        if (!mounted) {
            return (jsxs("div", { className: menuClassNames, children: [jsx("input", { type: "checkbox", value: "selected", id: "global-navigation-menu-no-js", role: "button", "aria-expanded": isOpen, ...triggerProps }), menuChildren] }));
        }
        return jsx("div", { className: menuClassNames, children: menuChildren });
    }
}

const getClassNameList$6 = classNames.bind(styles);
var GlobalNavigationMenuTriggerAnalyticsId;
(function (GlobalNavigationMenuTriggerAnalyticsId) {
    GlobalNavigationMenuTriggerAnalyticsId["EXPAND"] = "Expand";
    GlobalNavigationMenuTriggerAnalyticsId["COLLAPSE"] = "Collapse";
})(GlobalNavigationMenuTriggerAnalyticsId || (GlobalNavigationMenuTriggerAnalyticsId = {}));
class GlobalNavigationMenuTrigger extends React.Component {
    static displayName = 'GlobalNavigationMenuTrigger';
    static baseClassName = 'global-navigation-menu-trigger';
    render() {
        const { mounted, isOpen, children, onClick } = this.props;
        const localAnalyticsId = isOpen
            ? GlobalNavigationMenuTriggerAnalyticsId.COLLAPSE
            : GlobalNavigationMenuTriggerAnalyticsId.EXPAND;
        const childCount = React.Children.count(children);
        let additionalProps;
        if (!children || childCount !== 1) {
            console.warn(`GlobalNavigationMenuTrigger requires exactly one child component. ${childCount} children found.`);
            return null;
        }
        validateChildTypes(children, [
            EGDSButton,
            EGDSPrimaryButton,
            EGDSSecondaryButton,
            EGDSTertiaryButton,
            EGDSFloatingFullWidthButton,
            EGDSFloatingButton,
            EGDSFloatingActionButton,
            GlobalNavigationMenuIconTrigger,
        ], this.constructor.name);
        const menuTriggerChild = children;
        const { className, analytics } = menuTriggerChild.props;
        const wrapperClassNames = getClassNameList$6(GlobalNavigationMenuTrigger.baseClassName, className, isOpen ? `global-navigation-menu-active` : '');
        if (!mounted) {
            additionalProps = {
                'aria-hidden': 'true',
                className: wrapperClassNames,
                htmlFor: 'global-navigation-menu-no-js',
                tag: 'label',
            };
            return React.cloneElement(menuTriggerChild, additionalProps);
        }
        additionalProps = {
            analytics: analytics ? { localAnalyticsId, ...analytics } : undefined,
            'aria-expanded': isOpen,
            className: wrapperClassNames,
            onClick: () => {
                if (onClick) {
                    onClick();
                }
                if (menuTriggerChild.props.onClick) {
                    menuTriggerChild.props.onClick();
                }
            },
        };
        return React.cloneElement(menuTriggerChild, additionalProps);
    }
}

const getClassNameList$5 = classNames.bind(styles);
var PositionType;
(function (PositionType) {
    PositionType["RIGHT"] = "right";
    PositionType["LEFT"] = "left";
})(PositionType || (PositionType = {}));
class GlobalNavigationMenuContainer extends React.Component {
    static displayName = 'GlobalNavigationMenuContainer';
    static defaultProps = {
        position: PositionType.RIGHT,
    };
    render() {
        const { children, containerClasses, position, containerRef, isOpen } = this.props;
        const wrapperClassNames = getClassNameList$5(containerClasses, {
            [`global-navigation-menu-pos-${position}`]: validatePropType(position, PositionType),
        });
        return (jsx("div", { className: wrapperClassNames, ref: containerRef, "aria-hidden": !isOpen, children: children }));
    }
}

const getClassNameList$4 = classNames.bind(styles);
class GlobalNavigationMenuSection extends React.Component {
    static displayName = 'GlobalNavigationMenuSection';
    render() {
        const { children, className, label } = this.props;
        const wrapperClassNames = getClassNameList$4('global-navigation-menu-section', className);
        return (jsxs("div", { className: wrapperClassNames, children: [label ? jsx("h3", { children: label }) : '', children] }));
    }
}

class GlobalNavigationMenuListItem extends React.Component {
    static displayName = 'GlobalNavigationMenuListItem';
    render() {
        const { href, typeAnchor, label } = this.props;
        const Tag = typeAnchor ? 'a' : 'span';
        const { className, role, tabIndex, analytics, Link, to, icon } = this.props;
        const onClickCapture = analytics && analyticsTracker.bind(this, analytics);
        const additionalProps = {
            className,
            onClickCapture,
            role,
            tabIndex,
            to,
        };
        /* istanbul ignore next */
        if (Link && to) {
            return React.cloneElement(Link, additionalProps);
        }
        return className === `global-navigation-list-item` ? (jsx(EGDSLink, { children: jsxs(Tag, { ...additionalProps, ...(typeAnchor && { href }), children: [icon ? jsx(EGDSIcon, { name: icon }) : '', label] }) })) : (jsxs(Tag, { ...additionalProps, ...(typeAnchor && { href }), children: [icon ? jsx(EGDSIcon, { name: icon }) : '', label] }));
    }
}

class GlobalNavigationMenuList extends React.Component {
    static displayName = 'GlobalNavigationMenuList';
    render() {
        const { children, items } = this.props;
        return (jsxs("div", { role: "menu", className: "global-navigation-list uitk-type-400", children: [items &&
                    items.map((item, index) => (jsx(GlobalNavigationMenuListItem, { Link: item.Link, to: item.to, analytics: item.analytics, href: item.href, typeAnchor: item.typeAnchor, label: item.label, className: item.className, role: item.attributes && item.attributes.role, tabIndex: item.attributes && item.attributes.tabindex, icon: item.icon }, index))), children] }));
    }
}

const getClassNameList$3 = classNames.bind(styles);
class GlobalNavigationMenuIconTrigger extends React.Component {
    static displayName = 'GlobalNavigationMenuIconTrigger';
    static baseClassName = 'global-navigation-menu-icon-trigger';
    render() {
        const { className, icon, iconId, iconTitle, iconSize, text, ...props } = this.props;
        const wrapperClassNames = getClassNameList$3('global-navigation-menu-icon-trigger', className, {
            [`global-navigation-menu-icon-text-trigger`]: text,
        });
        return (jsxs(EGDSButton, { ...props, className: wrapperClassNames, children: [icon && jsx(EGDSIcon, { name: icon, title: iconTitle, id: iconId, size: iconSize }), text] }));
    }
}

const getClassNameList$2 = classNames.bind(styles);
const GlobalNavigationRow = (props) => {
    const { className, children, secondary, fullWidth } = props;
    const position = secondary ? 'secondary' : 'primary';
    const baseClassName = 'global-navigation-row';
    const wrapperClassNames = getClassNameList$2(baseClassName, position, className);
    const containerClassNames = getClassNameList$2(`${baseClassName}-container`, {
        [`${baseClassName}-container-fullWidth`]: fullWidth,
    });
    return (jsx("section", { className: wrapperClassNames, children: jsx("div", { className: containerClassNames, children: children }) }));
};
GlobalNavigationRow.displayName = 'GlobalNavigationRow';

const getClassNameList$1 = classNames.bind(styles);
const GlobalNavigationSiteFooter = (props) => {
    const { className, children } = props;
    const baseCssClass = 'global-navigation-footer';
    const wrapperClassNames = getClassNameList$1(baseCssClass, className);
    const containerClassNames = getClassNameList$1(`${baseCssClass}-container`);
    return (jsx("footer", { className: wrapperClassNames, children: jsx("div", { className: containerClassNames, children: children }) }));
};
GlobalNavigationSiteFooter.displayName = 'GlobalNavigationSiteFooter';

const getClassNameList = classNames.bind(styles);
const GlobalNavigationSiteHeader = (props) => {
    const { className, children } = props;
    const baseCssClass = 'global-navigation-site-header';
    const wrapperClassNames = getClassNameList(baseCssClass, className);
    const containerClassNames = getClassNameList(`${baseCssClass}-container`);
    return (jsx(EGDSContextProvider, { config: { context: 'global_navigation' }, children: jsx("header", { className: wrapperClassNames, children: jsx("div", { className: containerClassNames, children: children }) }) }));
};
GlobalNavigationSiteHeader.displayName = 'GlobalNavigationSiteHeader';

export { GlobalNavigationMenu as G, GlobalNavigationMenuTriggerAnalyticsId as a, GlobalNavigationMenuTrigger as b, GlobalNavigationMenuContainer as c, GlobalNavigationMenuSection as d, GlobalNavigationMenuList as e, GlobalNavigationMenuIconTrigger as f, GlobalNavigationRow as g, GlobalNavigationSiteFooter as h, GlobalNavigationSiteHeader as i };
//# sourceMappingURL=index-chunk.esm.js.map
